import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  Zap,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { getActiveClient } from '@/config/platform';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================
// SLA STATUS BADGE
// ============================================

export function SLAStatusBadge({ status, breach_level }) {
  const configs = {
    on_track: { 
      label: 'On Track', 
      variant: 'default',
      className: 'bg-green-100 text-green-700 border-green-200'
    },
    warning: { 
      label: 'Warning', 
      variant: 'warning',
      className: 'bg-amber-100 text-amber-700 border-amber-200'
    },
    breached: { 
      label: 'Breached', 
      variant: 'destructive',
      className: 'bg-orange-100 text-orange-700 border-orange-200'
    },
    critical: { 
      label: 'CRITICAL', 
      variant: 'destructive',
      className: 'bg-red-100 text-red-700 border-red-200 animate-pulse'
    }
  };

  const config = configs[status] || configs.on_track;

  return (
    <Badge 
      variant={config.variant}
      className={config.className}
      data-testid={`sla-badge-${status}`}
    >
      {breach_level > 0 && <AlertTriangle className="h-3 w-3 mr-1" />}
      {config.label}
    </Badge>
  );
}

// ============================================
// SLA PROGRESS BAR
// ============================================

export function SLAProgressBar({ hours_elapsed, warning_hours, breach_hours, critical_hours }) {
  const max_hours = critical_hours * 1.2;
  const percentage = Math.min(100, (hours_elapsed / max_hours) * 100);
  
  let color = 'bg-green-500';
  if (hours_elapsed >= critical_hours) color = 'bg-red-500';
  else if (hours_elapsed >= breach_hours) color = 'bg-orange-500';
  else if (hours_elapsed >= warning_hours) color = 'bg-amber-500';

  return (
    <div className="w-full" data-testid="sla-progress-bar">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{Math.round(hours_elapsed)}h elapsed</span>
        <span>{breach_hours}h breach</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {/* Threshold markers */}
      <div className="relative h-1 mt-1">
        <div 
          className="absolute w-px h-2 bg-amber-400" 
          style={{ left: `${(warning_hours / max_hours) * 100}%` }}
          title="Warning threshold"
        />
        <div 
          className="absolute w-px h-2 bg-orange-500" 
          style={{ left: `${(breach_hours / max_hours) * 100}%` }}
          title="Breach threshold"
        />
        <div 
          className="absolute w-px h-2 bg-red-600" 
          style={{ left: `${(critical_hours / max_hours) * 100}%` }}
          title="Critical threshold"
        />
      </div>
    </div>
  );
}

// ============================================
// SLA SUMMARY CARDS
// ============================================

export function SLASummaryCards({ summary }) {
  const cards = [
    { key: 'on_track', label: 'On Track', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
    { key: 'warning', label: 'Warning', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'breached', label: 'Breached', icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'critical', label: 'Critical', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' }
  ];

  return (
    <div className="grid grid-cols-4 gap-3" data-testid="sla-summary-cards">
      {cards.map(card => (
        <div 
          key={card.key}
          className={`${card.bg} rounded-lg p-3 text-center`}
        >
          <card.icon className={`h-5 w-5 mx-auto ${card.color}`} />
          <div className={`text-2xl font-bold ${card.color} mt-1`}>
            {summary[card.key] || 0}
          </div>
          <div className="text-xs text-gray-600">{card.label}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// AUTO-DISPATCH TOGGLE
// ============================================

export function AutoDispatchToggle({ enabled, onToggle }) {
  return (
    <div 
      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
      data-testid="auto-dispatch-toggle"
    >
      <div className="flex items-center gap-2">
        <Zap className={`h-5 w-5 ${enabled ? 'text-green-600' : 'text-gray-400'}`} />
        <div>
          <div className="font-medium text-sm">Auto-Dispatch</div>
          <div className="text-xs text-gray-500">
            {enabled ? 'Jobs will be auto-assigned to best contractors' : 'Manual assignment required'}
          </div>
        </div>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        data-testid="auto-dispatch-switch"
      />
    </div>
  );
}

// ============================================
// CONTROL ENGINE DASHBOARD (FULL)
// ============================================

export default function ControlEngineDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoDispatch, setAutoDispatch] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const clientId = getActiveClient();
      const res = await axios.get(`${API}/api/phs-control/dashboard?client_id=${clientId}`);
      setData(res.data);
      setAutoDispatch(res.data.settings?.auto_dispatch || false);
    } catch (error) {
      console.error('Error loading control dashboard:', error);
      toast.error('Failed to load control dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleToggleAutoDispatch = async (enabled) => {
    try {
      const clientId = getActiveClient();
      await axios.post(`${API}/api/phs-control/clients/${clientId}/toggle-auto-dispatch?enabled=${enabled}`);
      setAutoDispatch(enabled);
      toast.success(`Auto-dispatch ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling auto-dispatch:', error);
      toast.error('Failed to toggle auto-dispatch');
    }
  };

  const handleRunSLAMonitor = async () => {
    try {
      const res = await axios.post(`${API}/api/phs-control/sla/run-monitor`);
      toast.success(`SLA monitor complete: ${res.data.escalated} jobs escalated`);
      loadData();
    } catch (error) {
      console.error('Error running SLA monitor:', error);
      toast.error('Failed to run SLA monitor');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500 py-8">
            Failed to load control dashboard
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="control-engine-dashboard">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            PHS Control Engine
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRunSLAMonitor}
              data-testid="run-sla-monitor-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Run SLA Check
            </Button>
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-Dispatch Toggle */}
        <AutoDispatchToggle 
          enabled={autoDispatch}
          onToggle={handleToggleAutoDispatch}
        />

        {/* SLA Summary */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">SLA Status</div>
          <SLASummaryCards summary={data.sla_summary} />
        </div>

        {/* Jobs at Risk */}
        {data.jobs?.filter(j => j.sla.breach_level > 0).length > 0 && (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Jobs at Risk
            </div>
            <div className="space-y-2">
              {data.jobs
                .filter(j => j.sla.breach_level > 0)
                .slice(0, 5)
                .map(job => (
                  <div 
                    key={job.id}
                    className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100"
                    data-testid={`at-risk-job-${job.id}`}
                  >
                    <div>
                      <div className="font-medium text-sm">{job.job_reference}</div>
                      <div className="text-xs text-gray-500">{job.park} - {job.status}</div>
                    </div>
                    <SLAStatusBadge status={job.sla.status} breach_level={job.sla.breach_level} />
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="text-xs text-gray-400 text-right">
          {data.total_active_jobs} active jobs • Updated {new Date(data.timestamp).toLocaleTimeString()}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// COMPACT SLA WIDGET (For Dashboard)
// ============================================

export function SLACompactWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const clientId = getActiveClient();
        const res = await axios.get(`${API}/api/phs-control/dashboard?client_id=${clientId}`);
        setData(res.data);
      } catch (error) {
        console.error('Error loading SLA widget:', error);
      }
    };
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!data) return null;

  const atRisk = (data.sla_summary.warning || 0) + (data.sla_summary.breached || 0) + (data.sla_summary.critical || 0);

  return (
    <div 
      className="flex items-center gap-3 px-3 py-2 bg-white border rounded-lg"
      data-testid="sla-compact-widget"
    >
      {atRisk > 0 ? (
        <>
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <div>
            <div className="font-semibold text-sm">{atRisk} Jobs at Risk</div>
            <div className="text-xs text-gray-500">
              {data.sla_summary.critical || 0} critical, {data.sla_summary.breached || 0} breached
            </div>
          </div>
        </>
      ) : (
        <>
          <CheckCircle className="h-5 w-5 text-green-500" />
          <div>
            <div className="font-semibold text-sm text-green-700">All Jobs On Track</div>
            <div className="text-xs text-gray-500">{data.total_active_jobs} active</div>
          </div>
        </>
      )}
    </div>
  );
}
